// Marketing-bundle i18n — separate from ../translations.js (which is
// the authenticated /app product's i18n). Scoped here because the
// marketing bundle is a fully separate root render (see main.jsx) with
// its own component tree (Nav, Hero, Bento, Pricing, CTA, Footer...).
//
// Default language mirrors the product app's rule: Italian by default
// on the Italia tenant (branding.default_country === 'IT'), English
// everywhere else (main seidrplatform.com / other tenants). A header
// switcher (see Nav.tsx) lets the visitor flip to English.
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useOrg } from '../contexts/OrgContext';

export const marketingTranslations = {
  en: {
    nav: {
      demo: 'Demo',
      pricing: 'Pricing',
      openApp: 'Open app',
      openAppArrow: 'Open app →',
      signIn: 'Sign in',
      signUpFree: 'Sign up free',
    },
    hero: {
      eyebrow: 'Vertical AI · industrial siting',
      titlePre: 'Find sites where you can ',
      titleHighlight: 'actually build',
      lead: 'Hydrogen, PtX, and data center sites screened against grid, zoning, environment, utilities — in parallel.',
      bookDemo: 'Book a demo',
      tryLive: 'Try it live',
    },
    interactiveFilter: {
      eyebrow: 'Try it',
      title: 'Filter the entire Nordic grid.',
      liveDemo: 'Live · demo version',
    },
    bento: {
      eyebrow: 'The brain',
      title: "Built on data your competitors don't have.",
      gridIntelligenceLabel: 'Grid intelligence',
      gridIntelligenceDesc: 'Every Statnett & Fingrid line. Every queue position. Screened live.',
      statGwDesc: 'Norwegian data center grid queue — and growing',
      liveScoring: 'Live scoring',
      suitable: '/ 100 · Suitable',
      scoreCategories: ['Power', 'Env', 'Access'],
      sideBySideLabel: 'Side-by-side',
      sideBySideDesc: 'Every site, every metric, ranked at once — no spreadsheets.',
      scenariosDesc: 'More scenarios explored per project',
      dataSourcesLabel: 'Data sources',
      dataSources: ['NVE', 'Statnett · Fingrid', 'Artsdatabanken', 'Kartverket', 'Municipal plans'],
    },
    twoVerticals: {
      eyebrow: 'Two markets',
      title: 'One engine. Same bottleneck.',
      hydrogen: {
        label: 'Hydrogen & PtX',
        bullets: ['20–500 MW electrolyser', 'Ammonia · methanol · SAF', 'Pre-FEED engineering outputs'],
      },
      dataCenters: {
        label: 'Data centers',
        bullets: ['50–400 MW campus', 'Hyperscale & colocation', 'Free-cool climate analysis'],
      },
    },
    pricing: {
      eyebrow: 'Pricing',
      title: 'Free to explore. Pay when it ships.',
      subtext: 'Self-service Free and Pro · sales-assisted for Win the market',
      footnote: 'Monthly pricing, no annual commitment. Win the market is sales-assisted and shaped to your team — most decisions take under a week.',
      mostPopular: 'Most popular',
      tiers: {
        free: {
          name: 'Free',
          tagline: 'Try the engine',
          priceSuffix: 'forever',
          description: 'Screen the Stavanger region (Norway) for data center sites — see the real engine on a real region.',
          features: [
            'Stavanger region (Norway)',
            'Data Center use case',
            'Up to 3 saved projects',
            'Up to 25 results per screening',
            'All map layers',
            'Compare your saved projects',
          ],
          ctaLabel: 'Sign up free',
        },
        pro: {
          name: 'Pro',
          tagline: 'Site finding + project follow-up',
          priceSuffix: '/ month',
          description: 'Find sites across the full Nordic grid and track every project from screening to pre-FEED in one workspace. First month free.',
          features: [
            '1 month free trial',
            'All of Norway + Finland',
            'Hydrogen + Data Center use cases',
            'Unlimited saved projects',
            'Full result counts (no 25-site cap)',
            'LCOH heatmap + all map layers',
            'Custom polygon screening',
            'Advanced filters (Hazards · Species · Roads · Solar · Climate)',
            'Project Pipeline + Timeline follow-up',
            'PDF + XLSX export, data refresh on demand',
            'Email + Slack support',
          ],
          ctaLabel: 'Contact sales',
        },
        winTheMarket: {
          name: 'Win the market',
          tagline: 'The agentic workspace for industrial projects',
          pricePrefix: 'From',
          priceSuffix: '/ month',
          description: 'Everything in Pro, plus AI workflows for project development, due diligence, project finance and engineering assurance — the workspace where projects mature before major capital is committed.',
          features: [
            'Everything in Pro',
            'Project development workflows',
            'AI-assisted due diligence',
            'Project finance modelling + LCOH stress tests',
            'Engineering assurance review',
            'Priority support + onboarding',
          ],
          ctaLabel: 'Contact sales',
        },
      },
    },
    cta: {
      eyebrow: 'Book a demo',
      title: 'See SEIDR run against your sites.',
      requestReceived: 'Request received',
      thankYou: "Takk! We'll be in touch within one business day.",
      workEmail: 'Work email',
      company: 'Company',
      project: 'Project',
      companyPlaceholder: 'Your developer or industrial team',
      projectPlaceholder: 'e.g. 6 sites in Finnmark · 400 MW ammonia · Pre-FEED Q2',
      sending: 'Sending…',
      requestDemo: 'Request demo',
      submitFailed: "Couldn't submit",
      mailInstead: 'instead',
    },
    footer: {
      tagline: 'The engineering brain for early-phase industrial projects',
      partnership: 'Built in partnership with Respire Venture Studio',
    },
    heroShift: {
      consoleHeader: 'Site screening · Norway · 270 MW H₂',
      oldLabel: 'Old · linear',
      oldTitle: 'One parcel at a time',
      oldDesc: 'A consultant feasibility-checks one. It fails. Months pass. Start over.',
      checking: 'checking grid · zoning · env…',
      fail: 'fail',
      monthsPerCycle: 'months per cycle',
      newLabel: 'New · parallel',
      running: 'running…',
      done: 'done',
      newTitle: 'Every viable site, at once',
      newDesc: '17,127 parcels scored against grid, zoning, environment, queue — in seconds.',
      parcelsScreened: 'parcels screened',
      viableReady: 'viable · ready to build',
      live: 'Live',
      parcels: 'parcels',
      viable: 'Viable',
    },
  },
  it: {
    nav: {
      demo: 'Demo',
      pricing: 'Prezzi',
      openApp: 'Apri app',
      openAppArrow: 'Apri app →',
      signIn: 'Accedi',
      signUpFree: 'Registrati gratis',
    },
    hero: {
      eyebrow: 'AI verticale · localizzazione industriale',
      titlePre: 'Trova siti dove puoi ',
      titleHighlight: 'costruire davvero',
      lead: 'Siti per idrogeno, PtX e data center analizzati rispetto a rete elettrica, urbanistica, ambiente e infrastrutture — in parallelo.',
      bookDemo: 'Prenota una demo',
      tryLive: 'Provalo dal vivo',
    },
    interactiveFilter: {
      eyebrow: 'Provalo',
      title: "Filtra l'intera rete nordica.",
      liveDemo: 'Live · versione demo',
    },
    bento: {
      eyebrow: 'Il cervello',
      title: 'Costruito su dati che i tuoi concorrenti non hanno.',
      gridIntelligenceLabel: 'Intelligenza di rete',
      gridIntelligenceDesc: 'Ogni linea Statnett e Fingrid. Ogni posizione in coda. Analizzate in tempo reale.',
      statGwDesc: 'Coda di rete per data center in Norvegia — e in crescita',
      liveScoring: 'Punteggio live',
      suitable: '/ 100 · Idoneo',
      scoreCategories: ['Energia', 'Ambiente', 'Accesso'],
      sideBySideLabel: 'Confronto diretto',
      sideBySideDesc: 'Ogni sito, ogni metrica, classificati insieme — senza foglio di calcolo.',
      scenariosDesc: 'Più scenari esplorati per progetto',
      dataSourcesLabel: 'Fonti dei dati',
      dataSources: ['NVE', 'Statnett · Fingrid', 'Artsdatabanken', 'Kartverket', 'Piani comunali'],
    },
    twoVerticals: {
      eyebrow: 'Due mercati',
      title: 'Un solo motore. Lo stesso collo di bottiglia.',
      hydrogen: {
        label: 'Idrogeno e PtX',
        bullets: ['Elettrolizzatore 20–500 MW', 'Ammoniaca · metanolo · SAF', 'Output di ingegneria pre-FEED'],
      },
      dataCenters: {
        label: 'Data center',
        bullets: ['Campus 50–400 MW', 'Hyperscale e colocation', 'Analisi climatica free-cooling'],
      },
    },
    pricing: {
      eyebrow: 'Prezzi',
      title: 'Gratis da esplorare. Paghi quando è operativo.',
      subtext: 'Free e Pro self-service · Win the market assistito da vendite',
      footnote: 'Prezzi mensili, nessun impegno annuale. Win the market è assistito da vendite e su misura per il tuo team — la maggior parte delle decisioni richiede meno di una settimana.',
      mostPopular: 'Più popolare',
      tiers: {
        free: {
          name: 'Free',
          tagline: 'Prova il motore',
          priceSuffix: 'per sempre',
          description: 'Analizza la regione di Stavanger (Norvegia) per siti data center — vedi il motore reale su una regione reale.',
          features: [
            'Regione di Stavanger (Norvegia)',
            'Caso d\'uso Data Center',
            'Fino a 3 progetti salvati',
            'Fino a 25 risultati per analisi',
            'Tutti i livelli mappa',
            'Confronta i tuoi progetti salvati',
          ],
          ctaLabel: 'Registrati gratis',
        },
        pro: {
          name: 'Pro',
          tagline: 'Ricerca siti + monitoraggio progetti',
          priceSuffix: '/ mese',
          description: 'Trova siti su tutta la rete nordica e segui ogni progetto dalla selezione al pre-FEED in un unico spazio di lavoro. Primo mese gratis.',
          features: [
            '1 mese di prova gratuita',
            'Tutta la Norvegia + Finlandia',
            'Casi d\'uso Idrogeno + Data Center',
            'Progetti salvati illimitati',
            'Conteggio risultati completo (nessun limite di 25 siti)',
            'Heatmap LCOH + tutti i livelli mappa',
            'Analisi con poligono personalizzato',
            'Filtri avanzati (Rischi · Specie · Strade · Solare · Clima)',
            'Pipeline progetti + monitoraggio timeline',
            'Export PDF + XLSX, aggiornamento dati su richiesta',
            'Supporto via Email + Slack',
          ],
          ctaLabel: 'Contatta le vendite',
        },
        winTheMarket: {
          name: 'Win the market',
          tagline: 'Lo spazio di lavoro agentico per progetti industriali',
          pricePrefix: 'Da',
          priceSuffix: '/ mese',
          description: 'Tutto ciò che offre Pro, più flussi di lavoro AI per sviluppo progetti, due diligence, finanza di progetto e revisione tecnica — lo spazio di lavoro dove i progetti maturano prima di impegnare capitale significativo.',
          features: [
            'Tutto ciò che offre Pro',
            'Flussi di lavoro per lo sviluppo del progetto',
            'Due diligence assistita da AI',
            'Modellazione finanziaria del progetto + stress test LCOH',
            'Revisione di garanzia tecnica',
            'Supporto prioritario + onboarding',
          ],
          ctaLabel: 'Contatta le vendite',
        },
      },
    },
    cta: {
      eyebrow: 'Prenota una demo',
      title: 'Guarda SEIDR analizzare i tuoi siti.',
      requestReceived: 'Richiesta ricevuta',
      thankYou: 'Grazie! Ti risponderemo entro un giorno lavorativo.',
      workEmail: 'Email di lavoro',
      company: 'Azienda',
      project: 'Progetto',
      companyPlaceholder: 'Il tuo team di sviluppo o industriale',
      projectPlaceholder: 'es. 6 siti in Finnmark · 400 MW ammoniaca · Pre-FEED Q2',
      sending: 'Invio…',
      requestDemo: 'Richiedi demo',
      submitFailed: 'Invio non riuscito',
      mailInstead: 'invece',
    },
    footer: {
      tagline: "Il cervello ingegneristico per progetti industriali in fase iniziale",
      partnership: 'Realizzato in collaborazione con Respire Venture Studio',
    },
    heroShift: {
      consoleHeader: 'Analisi sito · Norvegia · 270 MW H₂',
      oldLabel: 'Vecchio · lineare',
      oldTitle: 'Una particella alla volta',
      oldDesc: 'Un consulente verifica la fattibilità di una particella. Fallisce. Passano mesi. Si ricomincia.',
      checking: 'verifica rete · urbanistica · ambiente…',
      fail: 'falliti',
      monthsPerCycle: 'mesi per ciclo',
      newLabel: 'Nuovo · parallelo',
      running: 'in corso…',
      done: 'completato',
      newTitle: 'Ogni sito idoneo, in un\'unica volta',
      newDesc: '17.127 particelle valutate rispetto a rete, urbanistica, ambiente, coda — in pochi secondi.',
      parcelsScreened: 'particelle analizzate',
      viableReady: 'idonei · pronti per costruire',
      live: 'Live',
      parcels: 'particelle',
      viable: 'Idonei',
    },
  },
};

const MarketingLangContext = createContext({
  language: 'en',
  setLanguage: () => {},
  t: (key) => key,
});

export function MarketingLangProvider({ children }) {
  const { branding, loading: orgLoading } = useOrg();
  const initialLanguage = branding?.default_country === 'IT' ? 'it' : 'en';
  const [language, setLanguageState] = useState(initialLanguage);
  const userOverride = useRef(false);

  // useState's initial value only applies on the very first render —
  // OrgContext's org/branding fetch is async, so `branding` (and thus
  // `initialLanguage`) is still the un-fetched default at that point.
  // Sync once the fetch resolves, unless the visitor already toggled
  // the switcher manually.
  useEffect(() => {
    if (!orgLoading && !userOverride.current) {
      setLanguageState(initialLanguage);
    }
  }, [orgLoading, initialLanguage]);

  const setLanguage = (lang) => {
    userOverride.current = true;
    setLanguageState(lang);
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = marketingTranslations[language];
    for (const k of keys) value = value?.[k];
    if (value !== undefined) return value;
    // Fallback to English for any key missing in the active language.
    let fallback = marketingTranslations.en;
    for (const k of keys) fallback = fallback?.[k];
    return fallback !== undefined ? fallback : key;
  };

  return (
    <MarketingLangContext.Provider value={{ language, setLanguage, t, initialLanguage }}>
      {children}
    </MarketingLangContext.Provider>
  );
}

export function useMarketingLang() {
  return useContext(MarketingLangContext);
}
